import { KanbanContractError } from '../contract';

export const REGIONS = [
  ['NORTE', 'Norte'],
  ['NORDESTE', 'Nordeste'],
  ['CENTRO_OESTE', 'Centro-Oeste'],
  ['SUDESTE', 'Sudeste'],
  ['SUL', 'Sul'],
].map(([value, label]) => ({ value, label }));
export const UFS =
  'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'
    .split(' ')
    .map((value) => ({ value, label: value }));
export const LINKS = [
  ['FROTA', 'Frota'],
  ['OURO', 'Ouro'],
  ['AGREGADO_CARRETA_COOPER', 'Agregado Carreta Cooper'],
  ['AGREGADO_CONJUNTO_COMPLETO', 'Agregado Conjunto Completo'],
  ['TERCEIRO', 'Terceiro'],
].map(([value, label]) => ({ value, label }));
export const LOAD_IDS = [
  'costCenterIds',
  'clientIds',
  'originCityIds',
  'destinationCityIds',
  'productIds',
  'vehicleTypeIds',
  'bodyTypeIds',
  'riskManagerIds',
];
/** Catálogos da seção Viagens; os dados cadastrais vêm da carga da viagem. */
export const TRAVEL_IDS = [
  'costCenterIds',
  'clientIds',
  'originCityIds',
  'destinationCityIds',
  'productIds',
];
export const PERIOD_DAYS = [7, 15, 30];
const object = (value) => {
  if (value == null) return {};
  if (typeof value !== 'object' || Array.isArray(value))
    throw new KanbanContractError('Filtros inválidos. Abra Filtro cargas para revisar a seleção.');
  return value;
};
const keys = (value, allowed) => {
  const data = object(value);
  if (Object.keys(data).some((key) => !allowed.includes(key)))
    throw new KanbanContractError('Filtro não reconhecido. Revise a seleção.');
  return data;
};
const selection = (value, valid) => {
  if (value == null) return [];
  if (!Array.isArray(value) || value.length > 100 || value.some((item) => !valid(item)))
    throw new KanbanContractError('Selecione até 100 opções válidas por campo.');
  return [...new Set(value)];
};
const ids = (value) =>
  selection(value, (item) => Number.isSafeInteger(item) && item > 0 && item <= 2147483647);
const domain = (value, options) =>
  selection(value, (item) => options.some((o) => o.value === item));
const booleans = (value) => {
  const result = selection(value, (item) => typeof item === 'boolean');
  return result.length === 2 ? [] : result;
};
// 5713: placa completa (7 letras ou números), sem hífen e em maiúsculas, como no backend (5712).
export const normalizePlate = (value) => String(value).trim().replace(/-/g, '').toUpperCase();
export const validPlate = (plate) => /^[A-Z0-9]{7}$/.test(plate);
const plates = (value) => {
  if (value == null) return [];
  if (!Array.isArray(value) || value.length > 100 || value.some((item) => typeof item !== 'string'))
    throw new KanbanContractError('Informe até 100 placas.');
  const list = [...new Set(value.map(normalizePlate))];
  if (!list.every(validPlate))
    throw new KanbanContractError('Informe placas completas, com 7 letras ou números.');
  return list;
};
export function validDay(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
const PERIOD_KEYS = { ALL: ['type'], DAYS: ['type', 'days'], RANGE: ['type', 'from', 'to'] };
/** Ausente vale 7 dias (primeiro acesso); o backend recebe sempre o período explícito. */
function period(value) {
  if (value == null) return { type: 'DAYS', days: 7 };
  const raw = object(value);
  if (!PERIOD_KEYS[raw.type])
    throw new KanbanContractError('Período inválido. Revise as datas dos filtros.');
  keys(raw, PERIOD_KEYS[raw.type]);
  if (raw.type === 'ALL') return { type: 'ALL' };
  if (raw.type === 'DAYS') {
    if (!PERIOD_DAYS.includes(raw.days))
      throw new KanbanContractError('Selecione 7, 15 ou 30 dias.');
    return { type: 'DAYS', days: raw.days };
  }
  const from = raw.from ?? null;
  const to = raw.to ?? null;
  if ([from, to].some((day) => day !== null && !validDay(day)))
    throw new KanbanContractError('Informe datas válidas.');
  if (from === null && to === null)
    throw new KanbanContractError('Informe a data inicial ou a data final.');
  if (from !== null && to !== null && from > to)
    throw new KanbanContractError('A data inicial deve ser igual ou anterior à final.');
  return { type: 'RANGE', from, to };
}
const group = (value, transit = false) => {
  const raw = keys(value, [
    'regions',
    'provinceUfs',
    'cityIds',
    'driverHasMopp',
    ...(transit ? ['estimatedDeliveryDates', 'scheduledDeliveryDates'] : []),
  ]);
  return {
    regions: domain(raw.regions, REGIONS),
    provinceUfs: domain(raw.provinceUfs, UFS),
    cityIds: ids(raw.cityIds),
    driverHasMopp: booleans(raw.driverHasMopp),
    ...(transit
      ? {
          estimatedDeliveryDates: selection(raw.estimatedDeliveryDates, validDay),
          scheduledDeliveryDates: selection(raw.scheduledDeliveryDates, validDay),
        }
      : {}),
  };
};
export function normalizeFilters(value) {
  if (typeof value === 'string') {
    if (value.length > 20000) throw new KanbanContractError('Filtros excedem o tamanho permitido.');
    try {
      value = JSON.parse(value);
    } catch {
      throw new KanbanContractError(
        'Não foi possível recuperar os filtros. Abra Filtro cargas para revisar.',
      );
    }
  }
  const raw = keys(value, ['loads', 'travels', 'candidates']);
  const loads = keys(raw.loads, [
    ...LOAD_IDS,
    'regions',
    'moppRequired',
    'trackingRequired',
    'loadIds',
    'period',
    'withAvailableVehicles',
  ]);
  if (loads.withAvailableVehicles !== undefined && typeof loads.withAvailableVehicles !== 'boolean')
    throw new KanbanContractError('Filtro de cargas com veículos disponíveis inválido.');
  const travels = keys(raw.travels, [
    ...TRAVEL_IDS,
    'travelIds',
    'regions',
    'moppRequired',
    'period',
    'plates',
  ]);
  const travelPlates = plates(travels.plates);
  const candidates = keys(raw.candidates, ['linkTypes', 'empty', 'inTransit']);
  // Mesma ordem de chaves do backend (control-tower-5455/filters.json).
  return {
    loads: {
      ...Object.fromEntries(LOAD_IDS.map((key) => [key, ids(loads[key])])),
      regions: domain(loads.regions, REGIONS),
      moppRequired: booleans(loads.moppRequired),
      trackingRequired: booleans(loads.trackingRequired),
      loadIds: ids(loads.loadIds),
      period: period(loads.period),
      // Só ligado entra no JSON, como no backend (5606): desligado não muda a consulta.
      ...(loads.withAvailableVehicles === true ? { withAvailableVehicles: true } : {}),
    },
    travels: {
      ...Object.fromEntries(TRAVEL_IDS.map((key) => [key, ids(travels[key])])),
      travelIds: ids(travels.travelIds),
      regions: domain(travels.regions, REGIONS),
      moppRequired: booleans(travels.moppRequired),
      period: period(travels.period),
      // Sem placas a chave não entra, como no backend (5712).
      ...(travelPlates.length ? { plates: travelPlates } : {}),
    },
    candidates: {
      linkTypes: domain(candidates.linkTypes, LINKS),
      empty: group(candidates.empty),
      inTransit: group(candidates.inTransit, true),
    },
  };
}
export const DEFAULT_FILTERS = normalizeFilters(null);
/** Campos com seleção; o período conta quando não é Todos. */
export const filterCount = (filters) =>
  [
    filters.loads,
    filters.travels,
    { linkTypes: filters.candidates.linkTypes },
    filters.candidates.empty,
    filters.candidates.inTransit,
  ].reduce(
    (n, section) =>
      n +
      Object.values(section).filter((value) =>
        Array.isArray(value) ? value.length : value.type !== 'ALL',
      ).length,
    0,
  );
/** Sempre explícito: sem ?filters= o Kanban usa os filtros salvos ou o padrão. */
export const serializedFilters = (filters) => JSON.stringify(filters);
export function sameFilters(left, right) {
  const canonical = (value) =>
    JSON.stringify(normalizeFilters(value), (_key, v) => (Array.isArray(v) ? [...v].sort() : v));
  return canonical(left) === canonical(right);
}
export function assertAppliedFilters(response, expected) {
  if (expected && (!response.appliedFilters || !sameFilters(response.appliedFilters, expected)))
    throw new KanbanContractError(
      'A consulta não confirmou os filtros aplicados. Atualize ou revise os filtros.',
    );
}
export const getField = (filters, path) =>
  path.split('.').reduce((value, key) => value[key], filters);
export function setField(filters, path, value) {
  const next = structuredClone(filters);
  const parts = path.split('.');
  const key = parts.pop();
  parts.reduce((node, part) => node[part], next)[key] = value;
  return normalizeFilters(next);
}
export function validateOptions(reply, field, page, perPage) {
  const p = reply?.pagination;
  if (
    reply?.version !== 1 ||
    reply.field !== field ||
    !Array.isArray(reply.data) ||
    !p ||
    p.page !== page ||
    p.perPage !== perPage ||
    !Number.isSafeInteger(p.totalCount) ||
    p.totalCount < 0 ||
    p.hasMore !== page * perPage < p.totalCount ||
    reply.data.length !== Math.max(0, Math.min(perPage, p.totalCount - (page - 1) * perPage))
  )
    throw new Error('Resposta de catálogo incompleta.');
  const seen = new Set();
  return {
    ...reply,
    data: reply.data.map((o) => {
      if (
        // O catálogo de placas (5717) usa a própria placa como valor.
        (field === 'plates'
          ? typeof o.value !== 'string' || !validPlate(o.value)
          : !Number.isSafeInteger(o.value) || o.value < 1) ||
        typeof o.label !== 'string' ||
        !o.label.trim() ||
        seen.has(o.value)
      )
        throw new Error('Opção inválida.');
      seen.add(o.value);
      return { value: o.value, label: [o.label, o.cnpj].filter(Boolean).join(' · ') };
    }),
  };
}
export function validateCities(reply) {
  if (!Array.isArray(reply)) throw new Error('Resposta de cidades inválida.');
  const seen = new Set();
  return reply.map((city) => {
    if (
      !Number.isSafeInteger(city.id) ||
      city.id < 1 ||
      typeof city.name !== 'string' ||
      !city.name.trim() ||
      !UFS.some((o) => o.value === city.province?.uf) ||
      seen.has(city.id)
    )
      throw new Error('Resposta de cidades incompleta.');
    seen.add(city.id);
    return { value: city.id, label: `${city.name} / ${city.province.uf}` };
  });
}
